import React, { useEffect, useRef } from "react";
import "./About.css";
import { useSEO } from "../components/SEO";

// ✅ Replace these later with your own images
import heroImg from "../assets/zebra.webp";
import aboutImg from "../assets/pvc exterior.webp"; // any premium image you already have

function About() {
  useSEO({
    title: "About Us | A-Z Blinds – 35+ Years of Trusted Window Blinds in Hyderabad",
    description: "A to Z Blinds – Crafting comfort and designing light for over 35 years. Precision-crafted roller, zebra, vertical and motorized blinds. Family-driven craftsmanship, premium materials, expert installation in Hyderabad.",
    keywords: "about A to Z Blinds, blinds company Hyderabad, window blinds experience, custom blinds Hyderabad, blind installation Hyderabad",
  });

  const containerRef = useRef(null);

  // ✅ Scroll-reveal animation (no library)
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    const els = root.querySelectorAll("[data-reveal]");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-visible");
        });
      },
      { threshold: 0.12 }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const values = [
    {
      title: "Precision Craftsmanship",
      desc: "Every blind is measured, finished, and installed with care.",
      icon: "✓",
    },
    {
      title: "35+ Years of Experience",
      desc: "Decades of trust built across homes and workspaces.",
      icon: "⟡",
    },
    {
      title: "500+ Design Options",
      desc: "Styles, textures, colors, and materials to match your space.",
      icon: "◻",
    },
    {
      title: "Smart & Motorized",
      desc: "Modern automation for effortless comfort and control.",
      icon: "⚡",
    },
    {
      title: "Fast Installation",
      desc: "Efficient service timelines without compromising quality.",
      icon: "↻",
    },
    {
      title: "After-Sales Support",
      desc: "We’re here for you even after installation.",
      icon: "☏",
    },
  ];

  const stats = [
    { big: "3", small: "DAYS", label: "Fast, efficient service within 3 days" },
    { big: "35+", small: "YEARS", label: "35 years of trusted service" },
    { big: "70%", small: "LOYALTY", label: "Repeat customers & long-term relations" },
    { big: "500+", small: "STYLES", label: "Design styles, colors, and materials" },
  ];

  return (
    <div className="about" ref={containerRef}>
      {/* HERO */}
      <section className="about-hero">
        <div className="about-hero__bg">
          <img src={heroImg} alt="About A to Z Blinds" />
          <div className="about-hero__overlay" />
        </div>

        <div className="about-hero__content">
          <h1 className="about-title" data-reveal>
            Crafting Comfort.
            <br />
            Designing Light.
          </h1>

          <p className="about-subtitle" data-reveal>
            For over three decades, A to Z Blinds has been redefining interiors with
            precision crafted window solutions that blend functionality, elegance, and innovation.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="about-section">
        <div className="wrap grid-2">
          <div className="media-card" data-reveal>
            <img src={aboutImg} alt="Our story" />
          </div>

          <div className="content" data-reveal>
            <p className="eyebrow">Our Journey</p>
            <h2 className="h2">A legacy of precision and trust.</h2>
            <p className="p">
              A to Z Blinds began as a family driven venture built on craftsmanship and service.
              Over the years, our work has grown into a promise to deliver blinds that elevate
              comfort, privacy, and design in every space.
            </p>
            <p className="p">
              Today, we serve homes, offices, and commercial spaces with modern solutions that feel
              seamless and premium from consultation to installation, and beyond.
            </p>

            <div className="quote" data-reveal>
              <div className="quote__line" />
              <p>
                “We don’t just install blinds we shape the mood of your space with light,
                texture, and detail.”
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="about-section about-section--soft">
        <div className="wrap">
          <div className="section-head" data-reveal>
            <p className="eyebrow">Why Choose Us</p>
            <h2 className="h2">Premium solutions, made effortless.</h2>
            <p className="p p--muted">
              Clean designs, precise installation, and long-lasting quality backed by experience.
            </p>
          </div>

          <div className="value-grid">
            {values.map((v) => (
              <div className="value-card" key={v.title} data-reveal>
                <div className="value-icon">{v.icon}</div>
                <div>
                  <h3 className="h3">{v.title}</h3>
                  <p className="p p--muted">{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="about-section">
        <div className="wrap grid-2">
          <div className="panel" data-reveal>
            <p className="eyebrow">Our Mission</p>
            <h2 className="h2">Elevate every space with thoughtful design.</h2>
            <p className="p">
              To enhance living and working environments through high-quality window solutions that
              combine durability, comfort, and modern design.
            </p>
          </div>

          <div className="panel" data-reveal>
            <p className="eyebrow">Our Vision</p>
            <h2 className="h2">Be the most trusted blinds partner.</h2>
            <p className="p">
              To be the preferred choice for premium blinds known for craftsmanship, reliability,
              and customer satisfaction across generations.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="about-stats">
        <div className="wrap stats-grid">
          <div className="stats-left">
            {stats.map((s) => (
              <div className="stat" key={s.big} data-reveal>
                <h3>
                  {s.big} <span>{s.small}</span>
                </h3>
                <p>{s.label}</p>
              </div>
            ))}
          </div>

          <div className="stats-right" data-reveal>
            <p className="eyebrow eyebrow--light">Our Promise</p>
            <h2 className="h2 h2--light">Designed to feel premium. Built to last.</h2>
            <p className="p p--light">
              We bring together modern design, smooth functionality, and precise installation
              so your blinds don’t just look great, they perform beautifully every day.
            </p>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="about-close">
        <div className="wrap close-box" data-reveal>
          <h2>Let light define your space.</h2>
          <p>
            Whether it’s a home, office, or commercial space we create window solutions that feel
            clean, modern, and perfectly tailored.
          </p>
        </div>
      </section>
    </div>
  );
}

export default About;
